// src/components/RichTextEditor.js
import React, { useMemo, useRef, useCallback, useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import { uploadFile, deleteFile } from '../utils/storage';
import 'react-quill/dist/quill.snow.css';

const TOOLBAR_OPTIONS = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ align: [] }],
  ['link', 'image'],
  ['clean'],
];

export default function RichTextEditor({ value, onChange, placeholder, gameId }) {
  const quillRef = useRef(null);
  // 이전 이미지 URL 목록을 ref로 저장 (렌더 트리거 없음)
  const prevImageUrlsRef = useRef([]);

  // URL ↔ 스토리지 경로 매핑
  const [imageMap, setImageMap] = useState({});
  // 업로드 중 표시 여부
  const [uploading, setUploading] = useState(false);

  // 이미지 업로드 핸들러
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setUploading(true);

      const fileName = `${gameId}_${file.name}`;
      const path = `games/${gameId}/${fileName}`;

      try {
        const url = await uploadFile(file, `games/${gameId}`, fileName);
        setImageMap(map => ({ ...map, [url]: path }));

        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true);
        editor.insertEmbed(range.index, 'image', url);
        editor.setSelection(range.index + 1);
      } catch (err) {
        console.error('Image upload failed:', err);
      } finally {
        setUploading(false);
      }
    };
  }, [gameId]);

  // Quill 모듈 및 핸들러 설정
  const modules = useMemo(
    () => ({ toolbar: { container: TOOLBAR_OPTIONS, handlers: { image: imageHandler } } }),
    [imageHandler]
  );

  const formats = useMemo(
    () => [
      'header', 'bold', 'italic', 'underline', 'strike',
      'list', 'bullet', 'indent', 'align',
      'link', 'image'
    ],
    []
  );

  // value 변경 감지: 삭제된 이미지 URL 찾아서 스토리지에서 삭제
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const ops = editor.getContents().ops || [];
    const newUrls = ops
      .filter(op => op.insert && op.insert.image)
      .map(op => op.insert.image);

    const removed = prevImageUrlsRef.current.filter(url => !newUrls.includes(url));
    removed.forEach(url => {
      const path = imageMap[url];
      if (path) {
        deleteFile(path).catch(console.error);
        setImageMap(map => {
          const next = { ...map };
          delete next[url];
          return next;
        });
      }
    });

    prevImageUrlsRef.current = newUrls;
  }, [value, imageMap]);

  return (
    <div className="relative bg-neutral-900 rounded">
      {uploading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
          <span className="text-white">업로드 중...</span>
        </div>
      )}
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        className="min-h-[250px] h-auto"
      />
    </div>
  );
}
